import { getData, postData } from './apiClient';
import {
  getCustomExercises,
  getExerciseLog,
  saveCustomExercises,
  saveExerciseLog,
  getWorkoutNotes,
  saveWorkoutNotes,
} from '../utils/localStorageUtils';
import { addDaysYMDLocal } from '../utils/dateUtils';
import { isClerkEnabled } from '../utils/clerkConfig';
import { mergeCustomExercises } from './customExerciseService';


export function mergeWorkoutNotes(localNotes = {}, remoteNotes = {}) {
  const merged = { ...(localNotes || {}) };
  for (const [date, note] of Object.entries(remoteNotes || {})) {
    if (typeof note === 'string' && note.trim().length > 0) {
      merged[date] = note.trim();
    }
  }
  return merged;
}

export function mergeExerciseLogs(localLog = {}, remoteLog = {}) {
  // Start with local data. For dates present in remote, we will replace the
  // entire day with a merged view that respects remote deletions.
  const merged = { ...localLog };

  for (const [date, remoteEntries] of Object.entries(remoteLog || {})) {
    const localDay = (merged && merged[date]) || {};
    const remoteDay = remoteEntries || {};

    // Remote is authoritative for which exercises exist on that date.
    // Build nextDay only from remote's exercises, resolving conflicts
    // by choosing the one with more completed sets.
    const nextDay = {};

    for (const [exerciseId, remoteSets] of Object.entries(remoteDay)) {
      const localSets = localDay[exerciseId];
      if (!localSets) {
        nextDay[exerciseId] = remoteSets;
        continue;
      }

      const localLen = Array.isArray(localSets) ? localSets.length : 0;
      const remoteLen = Array.isArray(remoteSets) ? remoteSets.length : 0;

      // Prefer the one with more completed sets; tie-breaker: keep remote
      // unless local has planned status changes that remote does not yet have.
      if (remoteLen > localLen) {
        nextDay[exerciseId] = remoteSets;
      } else if (localLen > remoteLen) {
        nextDay[exerciseId] = localSets;
      } else {
        const localPlannedCount = Array.isArray(localSets) ? localSets.filter((s) => s?.type === "planned").length : 0;
        const remotePlannedCount = Array.isArray(remoteSets) ? remoteSets.filter((s) => s?.type === "planned").length : 0;
        if (localPlannedCount !== remotePlannedCount) {
          nextDay[exerciseId] = localSets;
        } else {
          nextDay[exerciseId] = remoteSets;
        }
      }
    }

    // Preserve local exercises that contain planned sets (e.g. from workout planner or draft)
    // so they are not pruned before they are pushed to the remote backend.
    for (const [exerciseId, localSets] of Object.entries(localDay)) {
      if (!nextDay[exerciseId] && Array.isArray(localSets) && localSets.length > 0) {
        const hasPlanned = localSets.some((s) => s?.type === "planned");
        if (hasPlanned) {
          nextDay[exerciseId] = localSets;
        }
      }
    }

    // By assigning nextDay, any exercise missing from remote for this date
    // is removed locally (deletions propagate across devices).
    merged[date] = nextDay;
  }

  return merged;
}

let activePushPromise = null;

export function getActivePushPromise() {
  return activePushPromise;
}

// GET /data => { userId, data, updatedAt }
export async function pullAndMergeExerciseLog(getToken) {
  if (!isClerkEnabled) {
    return getExerciseLog();
  }

  // If a push is currently in-flight, await it before pulling so we don't fetch stale state
  if (activePushPromise) {
    try {
      await activePushPromise;
    } catch (_) {
      // Ignore push errors for pull continuation
    }
  }

  let res;
  try {
    res = await getData(getToken);
  } catch (error) {
    if (error?.message?.startsWith('AUTH_NOT_READY') || error?.message === 'AUTH_TIMEOUT') {
      return getExerciseLog();
    }
    throw error;
  }
  const remoteData = res && typeof res === 'object' ? res.data : null;
  let remoteLog = remoteData;
  let remoteCustomExercises = [];
  let remoteWorkoutNotes = {};
  // Some backends may store JSON as a string; attempt to parse
  if (remoteLog && typeof remoteLog === 'string') {
    try { remoteLog = JSON.parse(remoteLog); } catch (_) { /* ignore parse errors */ }
  }

  if (remoteLog && typeof remoteLog === 'object' && !Array.isArray(remoteLog) && remoteLog.exerciseLog) {
    remoteCustomExercises = Array.isArray(remoteLog.customExercises) ? remoteLog.customExercises : [];
    if (remoteLog.workoutNotes && typeof remoteLog.workoutNotes === 'object') {
      remoteWorkoutNotes = remoteLog.workoutNotes;
    }
    remoteLog = remoteLog.exerciseLog;
  }

  // Adjust future-date keys that are off by one day due to UTC vs local
  if (remoteLog && typeof remoteLog === 'object' && remoteLog !== null) {
    const adjusted = {};
    const tomorrow = addDaysYMDLocal(new Date(), 1);
    for (const [date, entries] of Object.entries(remoteLog)) {
      // If a date is exactly 'tomorrow' (relative to this device) and yesterday exists empty,
      // move it back one day to align with local intent. We only adjust by +1 day case.
      if (date === tomorrow) {
        const prev = addDaysYMDLocal(new Date(date), -1);
        // Only shift if previous day is not already present to avoid overwriting
        if (!remoteLog[prev] && !adjusted[prev]) {
          adjusted[prev] = entries;
          continue;
        }
      }
      adjusted[date] = entries;
    }
    remoteLog = adjusted;
  }

  if (remoteLog && typeof remoteLog === 'object') {
    const local = getExerciseLog();
    const merged = mergeExerciseLogs(local, remoteLog);
    saveExerciseLog(merged);
    const mergedCustomExercises = mergeCustomExercises(getCustomExercises(), remoteCustomExercises);
    saveCustomExercises(mergedCustomExercises);
    const mergedNotes = mergeWorkoutNotes(getWorkoutNotes(), remoteWorkoutNotes);
    saveWorkoutNotes(mergedNotes);
    return merged;
  }
  return getExerciseLog();
}

// POST /data with the full exercise log as the JSON body
export async function pushExerciseLog(getToken) {
  if (!isClerkEnabled) {
    return null;
  }

  const pushTask = (async () => {
    try {
      const log = getExerciseLog();
      const customExercises = getCustomExercises();
      const workoutNotes = getWorkoutNotes();
      return await postData(getToken, {
        exerciseLog: log,
        customExercises,
        workoutNotes,
      });
    } catch (error) {
      if (error?.message?.startsWith('AUTH_NOT_READY') || error?.message === 'AUTH_TIMEOUT') {
        return null;
      }
      throw error;
    }
  })();

  activePushPromise = pushTask;
  try {
    return await pushTask;
  } finally {
    if (activePushPromise === pushTask) {
      activePushPromise = null;
    }
  }
}
